
import z from 'zod';
import { API } from '../../apis/apiWrapper';
import { IAddress, getAddressSchema } from '../address';
import { IPhone} from '../phone';
import { isAIActive } from './constants';
import { processResponse } from '../../utils/api';
import { ISummary } from 'src/types';
import { IFieldErrors, ValidationError, EmailSchema, UrlSchema } from '../validation';
import { diff } from 'deep-object-diff';

interface IOrg {
  orgId: string;
  name: string;
  nameShortAbbr?: string;
  nameNick?: string;
  nameFormerOfficial?: string;
  nameRoot?: string;
  email: string;
  phone?: Partial<IPhone>;

  fax?: Partial<IPhone>;
  website?: string;
  visitingAddress: IAddress;
  shippingAddress?: IAddress;
  orgSubTypeCd: string;
  schoolSubTypeCd?: string;
  schoolLevelCd?: string;
  orgCodeNotes?: string;
}

export interface IOrgAI extends IOrg {
  aiCode: string,
  aiStatusCd: "1" | "2";
  aiLevelCd: "1" | "2";
  lowestGradeCd: string;
  highestGradeCd: string;
}

export interface IRequestMeta {
  creator: string;
  creatorEmail: string;
  disclaimer: boolean;
  submitter: string;
}

export interface IAIUpdate extends IOrgAI {
  isMerge?: boolean;
  merge?: {
    aiCode?: string;
    name?: string;
    address?: IAddress;
  };
  closureDate?: string;
  submitterComment?: string;
  deactivateDisclaimer?: boolean;
}

export interface IAIUpdateRequest {
  data: IAIUpdate;
  meta: IRequestMeta;
  original: Partial<IAIUpdate>;
  changes: Partial<IAIUpdate>;
}

export interface IOrgUpdate {
  creator: string;
  creatorEmail: string;
  orgId: string;
  name: string;
  email: string;
  phone?: Partial<IPhone>;
  fax?: Partial<IPhone>;
  website: string;
  isMerge?: boolean;
  merge?: {
    aiCode?: string;
    name?: string;
    address?: IAddress;
  };
  visitingAddress: IAddress;
  shippingAddress?: IAddress;
  closureDate?: string;
  lowestGradeCd: string;
  highestGradeCd: string;
  orgSubTypeCd: string;
  schoolSubTypeCd?: string;
  schoolLevelCd?: string;
  aiCode: string,
  aiStatusCd: "1" | "2";
  aiLevelCd: "1" | "2";
  disclaimer: boolean;
  submitter: string;
}

const disclaimerErrorMap: z.ZodErrorMap = (issue, ctx) => {
  if (issue.code === z.ZodIssueCode.invalid_literal) {
    if (issue.expected === true) {
      return { message: "You must agree to the disclaimer" };
    }
  }
  return { message: ctx.defaultError };
};

const customErrorMap: z.ZodErrorMap = (issue, ctx) => {
  if (issue.message)
    return { message: issue.message };

  if (issue.code === z.ZodIssueCode.too_small) {
    if (issue.type === 'string' && issue.minimum === 1) {
      return { message: "Field cannot be empty" };
    }
  }
  return { message: ctx.defaultError };
};

z.setErrorMap(customErrorMap);

const MetaSchema = z.object({
  creator: z.string().min(1),
  creatorEmail: EmailSchema,
  disclaimer: z.literal(true, {
    errorMap: disclaimerErrorMap
  }),
  submitter: z.string().min(1)
});

const baseSchema = z.object({
  orgId: z.string().min(1),
  name: z.string().min(1),
  email: EmailSchema,
  phone: z.object({
    formatted: z.string().min(7).max(22).optional()
  }),
  website: UrlSchema,
  lowestGradeCd: z.string().min(1),
  highestGradeCd: z.string().min(1),
  aiCode: z.string().length(6, "Field must be a code 6 characters long"),
  aiStatusCd: z.enum(['1', '2']),
  aiLevelCd: z.enum(['1', '2']),
  orgSubTypeCd: z.string(),
  isMerge: z.boolean().optional()
});

const ClosureDateSchema = z.string().regex(/\d{1,2}\/\d{1,2}\/\d{4}/, "Closure date must be in a format of DD/MM/YYYY");

const getDataSchema = (update: Partial<IAIUpdate>) => {
  const { visitingAddress, shippingAddress, merge, aiStatusCd } = update;
  const VisitingAddressSchema = getAddressSchema(visitingAddress?.countryIsoCd);
  const ShippingAddressSchema = getAddressSchema(shippingAddress?.countryIsoCd);
  const MergeAddressSchema = getAddressSchema(merge?.address?.countryIsoCd);

  const MergeSchema = z.object({
    aiCode: z.string().min(6),
    name: z.string().min(1),
    address: MergeAddressSchema,
  })
    .partial()
    .refine((data) => {
      return (!!data.aiCode || (!!data.name && !!data.address));
    }, "Requires either an AI Code or School Name & Address");

  const isClosureOptional = (!aiStatusCd || isAIActive(aiStatusCd));

  return baseSchema.extend({
    visitingAddress: VisitingAddressSchema,
    shippingAddress: ShippingAddressSchema.optional(),
    merge: MergeSchema.optional(),
    closureDate: isClosureOptional ? ClosureDateSchema.optional() : ClosureDateSchema,
    submitterComment: shippingAddress ? z.string().min(1) : z.string().optional(),
    deactivateDisclaimer: isClosureOptional ? z.undefined() : z.literal(true, {
      errorMap: disclaimerErrorMap,
    }),
    nameShortAbbr : z.string().optional(),
    nameNick: z.string().optional(),
    nameFormerOfficial: z.string().optional(),
    nameRoot: z.string().optional(),
    schoolSubTypeCd: z.string().optional(),
    schoolLevelCd: z.string().optional(),
    orgCodeNotes: z.string().optional()
  });
};


/**
 * Returns the Zod Validation Schema for the update request.  This is a factory
 * method due to the fact that it contains conditional validations and the schema
 * needs to be built based off certain selections
 */
const getSchema = (update: Partial<IAIUpdate>) => {
  const dataSchema = getDataSchema(update || {});
  return z.object({
    data: dataSchema,
    meta: MetaSchema
  });
};

export const validate = (update: Partial<IAIUpdate>, meta: Partial<IRequestMeta>, original: Partial<IAIUpdate>): IAIUpdateRequest => {
  const schema = getSchema(update);
  try {
    // Ensure there are changes from the original record
    const validated = schema.parse({ data: update, meta: meta });
    const changes = diff(original, validated.data);
    const hasChanges = Object.keys(changes).length > 0;
    if (!hasChanges) {
      throw new z.ZodError([
        {
          "code": "invalid_type",
          "expected": "object",
          "received": "undefined",
          "path": ["diff"],
          "message": "No changes were detected to this organization"
        }
      ]);
    }

    return {
      ...validated,
      original,
      changes
    };
  } catch (error: any) {
    if (error instanceof z.ZodError) {
      const errors: IFieldErrors = error.format();
      throw new ValidationError(errors);
    }
    throw new Error(error?.message || '');
  }
};

export const create = async (update: Partial<IAIUpdate>, meta: Partial<IRequestMeta>, original: Partial<IAIUpdate>): Promise<IAIUpdateRequest> => {
  const request = validate(update, meta, original);
  const response: IAIUpdateRequest = await API.post('AiFormRequest', '/save?key=submitUpdateRequest', {
    body: {
      ...request
    }
  }).then((response: IAIUpdateRequest) => processResponse(response));
  return response;
};

interface IActiveRequest {
  ids: string[];
}

export const active = async (params: IActiveRequest) => {
  const response: ISummary[] = await API.post('AiFormRequest', '/save?key=activeUpdateRequests', {
    body: params
  }).then((response: ISummary[]) => processResponse(response));
  return response;
};

/**
 *
 * @param params
 * @returns
 */
interface ISearchActive {
  active: ISummary[],
  owned: ISummary[];
}

export const searchActive = async (params: IActiveRequest) => {
  const response: ISearchActive = await API.post('AiFormRequest', '/save?key=getUpdateRequests', {
    body: params
  }).then((response: ISearchActive) => processResponse(response));
  return response;
};
